/**
 * ユーザー CSV のフォーマット定義。
 *
 * - 一括登録（import / AdminUserCsvImportComponent）用: `USER_CSV_HEADER_MAPPING`（6列）。
 *   テンプレート DL とヘッダー検証に使う。列構成を変える場合はここを直す。
 * - エクスポート（export / AdminUserListComponent）用: `USER_EXPORT_CSV_HEADER_MAPPING`。
 *   ユーザー一覧＋ライセンス有無の閲覧・出力用途。招待メール送信列は持たない。
 *   （将来 upsert/update を入れる際に import との列整合を再調整する想定）
 */

import { UserForAdmin } from '../../../../services/user/user.mapping'
import { RoleCode, roleCodeToJapanese } from '../../../../utils/types'

// ===== 一括登録（import）用 =====

// 一括登録 CSV の 1 行。キーは USER_CSV_HEADER_MAPPING の key と一致する。
export type UserCsvRow = {
  name: string
  email: string
  departmentName: string
  roles: string
  reportToEmail: string
  isInviteByMail: string
}

export const USER_CSV_HEADER_MAPPING: {
  label: string
  key: keyof UserCsvRow
}[] = [
  { label: '名前', key: 'name' },
  { label: 'メールアドレス', key: 'email' },
  { label: '部署名', key: 'departmentName' },
  { label: 'ロール（全権管理者、イネーブラー、ユーザー）', key: 'roles' },
  { label: '上司のメールアドレス', key: 'reportToEmail' },
  {
    label: '招待メール送信（送信する場合は 「送信」 と入力）',
    key: 'isInviteByMail',
  },
]

export const USER_CSV_HEADERS = USER_CSV_HEADER_MAPPING.map(
  (header) => header.label,
)

// ===== エクスポート（export）用 =====

// エクスポート CSV の 1 行。キーは USER_EXPORT_CSV_HEADER_MAPPING の key と一致する。
export type UserExportCsvRow = {
  name: string
  email: string
  departmentName: string
  roles: string
  reportToEmail: string
  license: string
}

export const USER_EXPORT_CSV_HEADER_MAPPING: {
  label: string
  key: keyof UserExportCsvRow
}[] = [
  { label: '名前', key: 'name' },
  { label: 'メールアドレス', key: 'email' },
  { label: '部署名', key: 'departmentName' },
  { label: 'ロール（全権管理者、イネーブラー、ユーザー）', key: 'roles' },
  { label: '上司のメールアドレス', key: 'reportToEmail' },
  { label: 'ライセンス', key: 'license' },
]

// 管理画面のユーザー情報をエクスポート CSV の 1 行へ変換する。
// roles は code を日本語ロール名へ変換して「、」で結合。license はライセンス割当の有無。
export const toUserExportCsvRow = (user: UserForAdmin): UserExportCsvRow => ({
  name: user.name,
  email: user.email,
  departmentName: user.departmentName,
  roles: user.roles
    .map((role) => {
      const japaneseRole = roleCodeToJapanese[role.code as RoleCode]
      if (!japaneseRole) {
        // 未知のロールコードを undefined のまま CSV に混ぜない（フォールバック禁止）
        // email は PII のため、id で特定する（Sentry 送信を考慮）
        throw new Error(
          `未知のロールコード "${role.code}" が含まれています (user: ${user.id})`,
        )
      }
      return japaneseRole
    })
    .join('、'),
  // 上司未設定は空欄
  reportToEmail: user.reportTo?.email ?? '',
  license: user.license ? 'あり' : 'なし',
})
